import * as React from 'react';
import {CourseObject, FaqObject} from 'api/course';
import {useFormikContext} from 'formik';
import Row from 'components/Row';
import Column from 'components/Column';
import Input, {InputVariant} from 'components/Input';
import Button, {ButtonVariant} from 'components/Button';
import FormContainer from '../../containers/FormContainer';
import TodoContainer from '../containers/TodoContainer';

/**
 * The faq form part of landing
 * @return {React.ReactElement}
 * @constructor
 */
export default function FaqForm(): React.ReactElement {
  const formContext = useFormikContext<Partial<CourseObject>>();

  /**
   * Render content
   * @return {JSX.Element}
   */
  function renderContent(): JSX.Element {
    /**
     * Render title field
     * @return {JSX.Element}
     */
    function renderTitle(): JSX.Element {
      /**
       * Handle input blur
       * @param {React.FocusEvent<HTMLInputElement>} event
       */
      function handleBlur(event: React.FocusEvent<HTMLInputElement>): void {
        formContext.setFieldValue('faq.title', event.target.value);
        formContext.submitForm();
      }

      return (
        <Row>
          <Column>
            <Row>
              <Column>
                <Input
                  type="text"
                  defaultValue={formContext.values.faq?.title}
                  label="Заголовок блока"
                  placeholder="Напишите ответы на часто задаваемые вопросы"
                  onBlur={handleBlur}
                  required
                />
              </Column>
            </Row>
          </Column>
        </Row>
      );
    }

    /**
     * Render list field
     * @return {JSX.Element}
     */
    function renderList() {
      /**
       * Render the faq.
       * @param {FaqObject} faq
       * @param {number} idx
       * @return {JSX.Element}
       */
      function renderFaq(faq: FaqObject, idx: number): JSX.Element {
        /**
         * Render question.
         * @return {JSX.Element}
         */
        function renderQuestion(): JSX.Element {
          /**
           * This blur event handler.
           * @param {React.FocusEvent<HTMLInputElement>} event
           */
          function handleBlur(
              event: React.FocusEvent<HTMLInputElement>,
          ): void {
            formContext.setFieldValue(
                `faq.list.${idx}.question`,
                event.target.value,
            );
            formContext.submitForm();
          }

          return (
            <Row>
              <Column>
                <Input
                  type="text"
                  value={formContext.values.faq?.list ? (
                    formContext.values.faq?.list[idx].question
                  ) : undefined}
                  label="Вопрос"
                  variant={InputVariant.Accent}
                  onChange={handleBlur}
                  placeholder="Вопрос"
                  required
                />
              </Column>
            </Row>
          );
        }

        /**
         * Render answer.
         * @return {JSX.Element}
         */
        function renderAnswer(): JSX.Element {
          /**
           * This blur event handler.
           * @param {React.FocusEvent<HTMLInputElement>} event
           */
          function handleBlur(
              event: React.FocusEvent<HTMLInputElement>,
          ): void {
            formContext.setFieldValue(
                `faq.list.${idx}.answer`,
                event.target.value,
            );
            formContext.submitForm();
          }

          return (
            <Row>
              <Column>
                <Input
                  type="text"
                  value={formContext.values.faq?.list ? (
                    formContext.values.faq?.list[idx].answer
                  ) : undefined}
                  label="Ответ"
                  variant={InputVariant.Accent}
                  onChange={handleBlur}
                  placeholder="Ответ"
                  required
                />
              </Column>
            </Row>
          );
        }

        /**
         * This delete event handler.
         */
        function handleDelete(): void {
          formContext.setFieldValue('faq.list', formContext.values.faq?.list ? [
            ...formContext.values.faq?.list.filter((item) => (
              item !== faq
            )),
          ] : []);
          formContext.submitForm();
        }

        return (
          <TodoContainer key={idx} onDelete={handleDelete}>
            {renderQuestion()}
            {renderAnswer()}
          </TodoContainer>
        );
      }

      /**
       * This click event handler.
       */
      function handleClick(): void {
        const emptyFaq: FaqObject = {
          question: '',
          answer: '',
        };

        formContext.setFieldValue('faq.list', formContext.values.faq?.list ? [
          ...formContext.values.faq?.list,
          emptyFaq,
        ] : [emptyFaq]);
        formContext.submitForm();
      }

      return (
        <Row>
          <Column>
            <Row>
              <Column>
                {formContext.values.faq?.list?.map((faq, idx) => (
                  renderFaq(faq, idx)
                ))}
              </Column>
            </Row>
            <Row>
              <Column>
                <Button
                  type="button"
                  variant={ButtonVariant.Outline}
                  onClick={handleClick}
                  stretched
                >
                  Добавить вопрос-ответ
                </Button>
              </Column>
            </Row>
          </Column>
        </Row>
      );
    }

    return (
      <Row>
        <Column>
          {renderTitle()}
          {renderList()}
        </Column>
      </Row>
    );
  }
  return (
    <FormContainer property="faq" label="Вопрос-ответ" collapse>
      {renderContent()}
    </FormContainer>
  );
}
